import type { Logger, LoggerConfig, LogLevel, LogContext } from './types';
import { LEVEL_PRIORITY, consoleSink, noopSink } from './types';
// noopSink referenciado para tornar export disponível via Logger module se necessário
void noopSink;

class LoggerImpl implements Logger {
  constructor(private cfg: LoggerConfig) {}

  private write(level: LogLevel, msg: string, ctx?: LogContext, err?: Error): void {
    if (LEVEL_PRIORITY[level] < LEVEL_PRIORITY[this.cfg.level]) return;
    this.cfg.sink({
      level,
      msg,
      ctx,
      errStack: err?.stack,
      ts: Date.now(),
    });
  }

  debug(msg: string, ctx?: LogContext): void {
    this.write('debug', msg, ctx);
  }

  info(msg: string, ctx?: LogContext): void {
    this.write('info', msg, ctx);
  }

  warn(msg: string, ctx?: LogContext): void {
    this.write('warn', msg, ctx);
  }

  error(msg: string, err?: Error, ctx?: LogContext): void {
    this.write('error', msg, ctx, err);
  }
}

function detectConfig(): LoggerConfig {
  const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV === true;
  return {
    level: isDev ? 'debug' : 'warn',
    sink: isDev ? consoleSink : noopSink,
  };
}

let _instance: Logger | null = null;

export function getLogger(): Logger {
  if (!_instance) _instance = new LoggerImpl(detectConfig());
  return _instance;
}

export function setLoggerForTests(impl: Logger): void {
  _instance = impl;
}

export function resetLoggerForTests(): void {
  _instance = null;
}

export function configureLogger(cfg: Partial<LoggerConfig>): void {
  const base = detectConfig();
  const merged: LoggerConfig = {
    level: cfg.level ?? base.level,
    sink: cfg.sink ?? base.sink,
  };
  _instance = new LoggerImpl(merged);
}

/** Proxy resolves to current singleton on each access (allows test overrides). */
export const logger: Logger = new Proxy({} as Logger, {
  get(_t, prop: keyof Logger) {
    const inst = getLogger();
    const fn = inst[prop];
    return typeof fn === 'function' ? fn.bind(inst) : fn;
  },
});
